import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, BarChart3, BedDouble, Users, Coins, TrendingUp, Building2, KeyRound,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import { toast } from "sonner";
import { todayLocalIso } from "@/lib/dateUtils";

const PIE_COLORS = ["#1B4332", "#2D6A4F", "#40916C", "#74C69D", "#95D5B2"];
const fmtNum = (v) => (v || 0).toLocaleString();

export default function HousingAnalytics() {
  const navigate = useNavigate();
  const today = todayLocalIso();
  const sixMonthsAgo = new Date(Date.now() - 180 * 86400000).toISOString().slice(0, 10);
  const [fromDate, setFromDate] = useState(sixMonthsAgo);
  const [toDate, setToDate] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
      const { data } = await api.get(`/housing/analytics?${params.toString()}`);
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load analytics"); }
    finally { setLoading(false); }
  }, [fromDate, toDate]);
  useEffect(() => { load(); }, [load]);

  const occupancyChartData = useMemo(
    () => (data?.occupancy || []).map(o => ({
      name: o.name,
      "Booked %": o.booked_pct ?? o.occupancy_pct,
      "Leased %": o.leased_pct ?? 0,
      nights: o.nights_booked,
    })),
    [data]
  );
  const tierPieKES = useMemo(
    () => (data?.revenue_by_tier || []).filter(t => t.KES > 0).map(t => ({ name: t.label, value: t.KES })),
    [data]
  );
  const tierPieUSD = useMemo(
    () => (data?.revenue_by_tier || []).filter(t => t.USD > 0).map(t => ({ name: t.label, value: t.USD })),
    [data]
  );

  return (
    <div className="space-y-6 fade-in" data-testid="housing-analytics-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
            Housing Analytics
          </h1>
          <p className="text-[#525C58] mt-1">Occupancy, revenue and agency performance.</p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" data-testid="analytics-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" data-testid="analytics-to" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#7A8A84]">Loading…</div>
      ) : !data ? (
        <p className="text-[#7A8A84]">No data</p>
      ) : (
        <>
          {/* Summary tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: BedDouble, label: "Total bookings", value: fmtNum(data.summary.total_bookings), color: "text-[#1B4332]" },
              { icon: KeyRound, label: "Active leases", value: fmtNum(data.summary.active_leases), color: "text-amber-700" },
              { icon: Users, label: "Visitor-nights", value: fmtNum(data.summary.total_visitor_nights), color: "text-[#2D6A4F]" },
              { icon: KeyRound, label: "Lease-nights", value: fmtNum(data.summary.total_lease_nights), color: "text-amber-600" },
              { icon: Coins, label: "Revenue KES", value: fmtNum(data.summary.revenue_KES), color: "text-emerald-700",
                sub: `Bookings ${fmtNum(data.summary.booking_revenue_KES)} · Leases ${fmtNum(data.summary.lease_revenue_KES)}` },
              { icon: Coins, label: "Revenue USD", value: `$${fmtNum(data.summary.revenue_USD)}`, color: "text-blue-700",
                sub: `Bookings $${fmtNum(data.summary.booking_revenue_USD)} · Leases $${fmtNum(data.summary.lease_revenue_USD)}` },
            ].map((tile) => {
              const Icon = tile.icon;
              return (
                <Card key={tile.label} className="border-[#E8E8E6]" data-testid={`stat-${tile.label.toLowerCase().replace(/\s/g, '-')}`}>
                  <CardContent className="pt-6">
                    <Icon className={`h-5 w-5 mb-3 ${tile.color}`} />
                    <p className="text-2xl font-semibold tabular-nums" style={{ fontFamily: "Manrope, sans-serif" }}>{tile.value}</p>
                    <p className="text-xs text-[#7A8A84] mt-1">{tile.label}</p>
                    {tile.sub && <p className="text-[10px] text-[#7A8A84] mt-1">{tile.sub}</p>}
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Occupancy bar */}
          <Card className="border-[#E8E8E6]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <BarChart3 className="h-5 w-5 text-[#40916C]" /> Occupancy by unit
              </CardTitle>
            </CardHeader>
            <CardContent>
              {occupancyChartData.length === 0 ? (
                <p className="text-center text-[#7A8A84] py-8">No bookings or leases in this date range yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={Math.max(200, occupancyChartData.length * 50)}>
                  <BarChart data={occupancyChartData} layout="vertical" margin={{ top: 10, left: 20, right: 30, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
                    <XAxis type="number" tickFormatter={(v) => `${v}%`} />
                    <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(value, name) => name.includes("%") ? `${value}%` : value} />
                    <Legend />
                    <Bar dataKey="Booked %" stackId="occ" fill="#1B4332" />
                    <Bar dataKey="Leased %" stackId="occ" fill="#D4A017" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Revenue by tier (two pies, KES + USD) */}
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Revenue by tier — KES", data: tierPieKES, suffix: " KES" },
              { title: "Revenue by tier — USD", data: tierPieUSD, suffix: " USD" },
            ].map((c) => (
              <Card key={c.title} className="border-[#E8E8E6]">
                <CardHeader><CardTitle className="text-base">{c.title}</CardTitle></CardHeader>
                <CardContent>
                  {c.data.length === 0 ? (
                    <p className="text-center text-[#7A8A84] py-8">No revenue in this currency.</p>
                  ) : (
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={c.data} dataKey="value" nameKey="name" outerRadius={80} label={(e) => e.name}>
                          {c.data.map((_, idx) => <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v) => fmtNum(v) + c.suffix} />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Monthly trend */}
          <Card className="border-[#E8E8E6]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="h-5 w-5 text-[#40916C]" /> Monthly revenue trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data.monthly_trend || []).length === 0 ? (
                <p className="text-center text-[#7A8A84] py-8">Not enough data yet.</p>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={data.monthly_trend} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E8E8E6" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={fmtNum} />
                    <Tooltip formatter={(v) => fmtNum(v)} />
                    <Legend />
                    <Line type="monotone" dataKey="KES" stroke="#1B4332" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="USD" stroke="#2563EB" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Leases in period */}
          <Card className="border-[#E8E8E6]" data-testid="analytics-leases-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <KeyRound className="h-5 w-5 text-amber-600" /> Leases in period
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data.leases || []).length === 0 ? (
                <p className="text-center text-[#7A8A84] py-8">No leases overlapping this date range.</p>
              ) : (
                <div className="space-y-2">
                  {data.leases.map((l) => (
                    <div key={l.lease_id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-lg bg-[#F4F4F1]/40 border border-[#E8E8E6]" data-testid={`lease-row-${l.lease_id}`}>
                      <div className="flex items-center gap-3">
                        <Badge className={l.status === "active" ? "bg-emerald-700 text-white border-0" : "bg-[#7A8A84] text-white border-0"}>
                          {l.status}
                        </Badge>
                        <div>
                          <p className="font-medium text-[#1C1F1E]">{l.tenant_name}</p>
                          <p className="text-xs text-[#7A8A84]">
                            {l.unit_name} · {l.start_date} → {l.end_date || "open-ended"} · {fmtNum(l.nights_in_window)} nights in window
                          </p>
                        </div>
                      </div>
                      <div className="text-sm text-right tabular-nums">
                        <p className="text-xs text-[#7A8A84]">{l.rent_currency} {fmtNum(l.monthly_rent)}/mo</p>
                        {l.revenue_KES > 0 && <p>KES {fmtNum(l.revenue_KES)}</p>}
                        {l.revenue_USD > 0 && <p className="text-[#7A8A84]">${fmtNum(l.revenue_USD)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top agencies */}
          <Card className="border-[#E8E8E6]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-[#40916C]" /> Top sending sources
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(data.top_agencies || []).length === 0 ? (
                <p className="text-center text-[#7A8A84] py-8">No bookings linked to a request yet.</p>
              ) : (
                <div className="space-y-2">
                  {data.top_agencies.map((a, idx) => (
                    <div key={a.name} className="flex items-center justify-between p-3 rounded-lg bg-[#F4F4F1]/40 border border-[#E8E8E6]">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-[#1B4332] text-white border-0">{idx + 1}</Badge>
                        <span className="font-medium text-[#1C1F1E]">{a.name}</span>
                      </div>
                      <div className="text-sm text-right tabular-nums">
                        {a.KES > 0 && <p>KES {fmtNum(a.KES)}</p>}
                        {a.USD > 0 && <p className="text-[#7A8A84]">${fmtNum(a.USD)}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
